import { formatInTimeZone } from 'date-fns-tz';

import { DEFAULT_TIMEZONE } from '../constants';
import type { DateInput, FormatString, TimezoneString } from '../types';

/**
 * Normalizes various date inputs into a native Date object.
 *
 * @param input - A Date object, string, or number.
 * @returns A Date object.
 */
export function toDate(input: DateInput): Date {
  if (input instanceof Date) return input;

  return new Date(input);
}

/**
 * Formats a date using a custom format string and optional timezone.
 *
 * @param date - The date to format.
 * @param pattern - The desired output format (e.g., 'yyyy-MM-dd HH:mm').
 * @param timezone - The target timezone (defaults to 'UTC').
 * @returns A formatted date string in the specified pattern and timezone.
 */
export function formatWithPattern(
  date: DateInput,
  pattern: FormatString,
  timezone: TimezoneString = DEFAULT_TIMEZONE,
): string {
  const d = toDate(date);
  return formatInTimeZone(d, timezone, pattern);
}

/**
 * Converts a date to an ISO 8601 string for API usage.
 *
 * @param date - The date to format.
 * @returns The date as an ISO 8601 string (e.g., '2024-01-15T10:00:00.000Z').
 */
export function formatForAPI(date: DateInput): string {
  return toDate(date).toISOString();
}

/**
 * Formats a date into a short, human-readable string for UI display.
 *
 * @param date - The date to format.
 * @param locale - The locale to use (default is 'en-US').
 * @param timezone - The target timezone (defaults to 'UTC').
 * @returns A formatted string like 'Jan 15, 2024'.
 */
export function formatForDisplay(
  date: DateInput,
  locale: string = 'en-US',
  timezone: TimezoneString = DEFAULT_TIMEZONE,
): string {
  const jsDate = toDate(date);

  return jsDate.toLocaleDateString(locale, {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
